import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, productImage } from "../lib/brand";
import { useCart } from "../lib/cart";
import { useAuth, authHeaders } from "../lib/auth";
import { ArrowLeft, Plus, Check, Play, Pause, ShoppingBag, Disc3, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PREVIEW_SECONDS = 15;

function TrackRow({ track, index, isPlaying, onTogglePlay }) {
  const { add, has } = useCart();
  const inCart = has(track.id);
  return (
    <div
      data-testid={`track-row-${track.id}`}
      className="grid grid-cols-[40px_36px_1fr_auto_auto] items-center gap-3 px-4 py-3 border-b border-[#1A2B20] hover:bg-[#0d1a13] transition-colors"
    >
      <div className="text-[#5C7064] text-sm font-mono text-right">{track.track_number ?? index + 1}</div>
      <button
        data-testid={`track-play-${track.id}`}
        onClick={onTogglePlay}
        disabled={!track.audio_path}
        className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
          track.audio_path ? "bg-[#1E7040] hover:bg-[#165932] text-white" : "bg-[#1A2B20] text-[#5C7064] cursor-not-allowed"
        }`}
        title={track.audio_path ? "Play 15-sec preview" : "No preview available"}
      >
        {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
      </button>
      <Link to={`/product/${track.id}`} className="min-w-0">
        <div className="text-white text-sm truncate hover:text-[#279153]">{track.title}</div>
        <div className="text-xs text-[#5C7064] truncate">{track.description}</div>
      </Link>
      <div className="text-sm text-white font-serif-display">${Number(track.price).toFixed(2)}</div>
      <button
        data-testid={`track-add-${track.id}`}
        disabled={inCart}
        onClick={() => add(track)}
        className={`px-3 py-2 text-[10px] tracking-[0.25em] uppercase ${
          inCart ? "bg-[#1A2B20] text-[#95A89C]" : "bg-[#1E7040] hover:bg-[#165932] text-white"
        }`}
      >
        {inCart ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

export default function AlbumDetail() {
  const { id } = useParams();
  const { add, has } = useCart();
  const { user } = useAuth();
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [bundleBuying, setBundleBuying] = useState(false);
  const [showBundleForm, setShowBundleForm] = useState(false);
  const [bundleEmail, setBundleEmail] = useState("");
  const audioRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/albums/${id}`)
      .then((r) => setAlbum(r.data))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (user?.email) setBundleEmail(user.email);
  }, [user]);

  // Stop audio when navigating away
  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const togglePlay = (track) => {
    if (!track.audio_path) return;
    if (playingId === track.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(`${API}/preview/audio/${track.id}`);
    a.addEventListener("timeupdate", () => {
      if (a.currentTime >= PREVIEW_SECONDS) {
        a.pause();
        setPlayingId(null);
      }
    });
    a.addEventListener("ended", () => setPlayingId(null));
    audioRef.current = a;
    a.play().catch(() => setPlayingId(null));
    setPlayingId(track.id);
  };

  const buyWholeAlbum = async () => {
    if (!bundleEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(bundleEmail)) {
      toast.error("Please enter a valid email");
      return;
    }
    setBundleBuying(true);
    try {
      const res = await axios.post(
        `${API}/checkout/create-session`,
        { album_id: album.id, email: bundleEmail, origin_url: window.location.origin },
        { headers: authHeaders() },
      );
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBundleBuying(false);
    }
  };

  if (loading) return <div className="pt-40 px-6 text-[#95A89C]">Loading…</div>;
  if (!album) return <div className="pt-40 px-6 text-white">Album not found</div>;

  const cover = album.cover_path
    ? `${API}/files/${album.cover_path}`
    : (album.tracks?.[0] && productImage(album.tracks[0]));

  return (
    <div data-testid="album-page" className="pt-32 pb-24 max-w-6xl mx-auto px-6 lg:px-12">
      <Link to="/music" className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-[#95A89C] hover:text-[#279153] mb-10">
        <ArrowLeft className="w-4 h-4" /> All music
      </Link>

      <div className="grid md:grid-cols-[320px_1fr] gap-10 mb-12">
        <div className="relative aspect-square bg-black border border-[#1A2B20] overflow-hidden">
          {cover ? (
            <img src={cover} alt={album.title} className="absolute inset-0 w-full h-full object-cover" draggable="false" onContextMenu={(e) => e.preventDefault()} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center"><Disc3 className="w-24 h-24 text-[#1A2B20]" /></div>
          )}
          <div className="absolute bottom-3 right-3 bg-black px-2.5 py-1.5">
            <img src={`${API}/brand/wordmark.png`} alt="Grá" className="h-6 w-auto object-contain" />
          </div>
        </div>
        <div className="flex flex-col">
          <div className="inline-flex items-center gap-2 mb-3">
            <Disc3 className="w-4 h-4 text-[#279153]" />
            <span className="text-[10px] tracking-[0.4em] uppercase text-[#279153]">Album</span>
          </div>
          <h1 data-testid="album-title" className="font-serif-display text-5xl text-white leading-tight mb-4">{album.title}</h1>
          <p className="text-base text-[#95A89C] leading-relaxed mb-6">{album.description}</p>
          <div className="text-sm text-[#95A89C] mb-6">
            {album.track_count} track{album.track_count === 1 ? "" : "s"}
            {album.album_price != null && (
              <> · whole album <span className="font-serif-display text-white">${Number(album.album_price).toFixed(2)}</span></>
            )}
          </div>

          {album.album_price != null && album.tracks?.length > 0 && (() => {
            const individualSum = album.tracks.reduce((s, t) => s + Number(t.price || 0), 0);
            const savings = individualSum - Number(album.album_price);
            return (
              <div data-testid="album-bundle-cta" className="border border-[#1A2B20] bg-[#0d1a13] p-5 mb-4">
                <div className="flex items-center justify-between gap-4 mb-3">
                  <div>
                    <div className="font-serif-display text-2xl text-white">Buy the whole album</div>
                    <div className="text-sm text-[#95A89C]">
                      Bundle price <span className="text-white font-serif-display">${Number(album.album_price).toFixed(2)}</span>
                      {savings > 0.005 && (
                        <span className="text-[#279153]"> · save ${savings.toFixed(2)} vs. buying tracks individually</span>
                      )}
                    </div>
                  </div>
                </div>

                {!showBundleForm ? (
                  <button
                    data-testid="album-buy-bundle-open"
                    onClick={() => setShowBundleForm(true)}
                    className="w-full px-6 py-3 bg-[#1E7040] hover:bg-[#165932] text-white text-xs tracking-[0.3em] uppercase flex items-center justify-center gap-2"
                  >
                    <ShoppingBag className="w-4 h-4" /> Buy entire album
                  </button>
                ) : (
                  <div className="space-y-3">
                    <input
                      data-testid="album-bundle-email"
                      type="email"
                      placeholder="Email for receipt"
                      value={bundleEmail}
                      onChange={(e) => setBundleEmail(e.target.value)}
                      className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-sm focus:border-[#279153] focus:outline-none"
                    />
                    <div className="flex gap-2">
                      <button
                        data-testid="album-bundle-checkout"
                        onClick={buyWholeAlbum}
                        disabled={bundleBuying}
                        className="flex-1 px-5 py-3 bg-[#1E7040] hover:bg-[#165932] text-white text-xs tracking-[0.3em] uppercase disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {bundleBuying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
                        Pay with Stripe
                      </button>
                      <button
                        onClick={() => setShowBundleForm(false)}
                        className="px-4 py-3 border border-white/20 text-white text-xs tracking-[0.3em] uppercase hover:border-[#95A89C]"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      <div className="bg-[#09120C] border border-[#1A2B20]">
        <div className="px-4 py-3 border-b border-[#1A2B20] text-[10px] tracking-[0.3em] uppercase text-[#5C7064]">
          Tracks
        </div>
        {album.tracks?.length ? (
          album.tracks.map((t, i) => (
            <TrackRow key={t.id} track={t} index={i} isPlaying={playingId === t.id} onTogglePlay={() => togglePlay(t)} />
          ))
        ) : (
          <div className="p-8 text-center text-sm text-[#95A89C]">No tracks released on this album yet.</div>
        )}
      </div>

      <div className="mt-8 p-5 bg-[#09120C] border border-[#1A2B20]">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Authenticity</div>
        <p className="text-sm text-[#95A89C] leading-relaxed">
          Each track plays as a 15-second preview. After purchase you receive the full, un-watermarked, high-quality audio file direct from the artist.
        </p>
      </div>
    </div>
  );
}
